// src/pages/SignOff.jsx
// Phase R10 — Client Portal & OTP-Verified Sign-off
// Public page — no authentication required.
// Director opens this link from their email to review and sign the AFS
// with OTP verification (email is the primary channel; SMS backup is
// admin-togglable via sms_otp_backup_enabled once a real provider is
// connected — see kailux-fixes-log.md §21).
import { useState, useEffect, useRef } from 'react'
import { Link2, Mail } from 'lucide-react'
import { useParams } from 'react-router-dom'
import { getSignOff, requestOTP, verifyAndSign } from '../api/kailux'
import { useFeatureFlag } from '../hooks/useFeatureFlag'

import Icon from '../components/Icon'
import { T, alpha } from '../theme/theme'
// ── LIGHT THEME — designed for the client (not the accountant) ─────────────

const ENG_LABELS = {
  compilation: 'Compiled by',
  review:      'Reviewed by',
  audit:       'Audited by',
  accounting_officer: 'Reported on by',
  in_house:    'Prepared in-house by',
}

// ── PLAIN-ENGLISH FIGURE FORMATTERS ──────────────────────────────────────────
export function fmtRand(n) {
  if (!n || n === 0) return 'R0'
  const abs = Math.abs(n)
  let s
  if (abs >= 1_000_000)      s = `R${(abs / 1_000_000).toFixed(2)}m`
  else if (abs >= 1_000)     s = `R${(abs / 1_000).toFixed(0)}k`
  else                       s = `R${abs.toFixed(0)}`
  return n < 0 ? `(${s})` : s
}

export function trendWord(current, prior) {
  if (!prior || prior === 0) return null
  const pct = ((current - prior) / Math.abs(prior)) * 100
  if (Math.abs(pct) < 1) return null
  return { pct: Math.round(Math.abs(pct)), up: pct > 0 }
}

export function extractFigures(fig = {}) {
  const r = (k) => Math.abs(parseFloat(fig[k] || 0))
  const revenue       = r('Revenue')
  const cogs          = r('Cost of Sales')
  const grossProfit   = revenue - cogs
  const opex = ['Employee Costs','Occupancy Costs','Communication Expenses',
    'Travel and Motor Expenses','General and Admin Expenses','Professional Fees',
    'Depreciation and Amortisation','Credit Losses','Sales and Marketing']
    .reduce((s, k) => s + r(k), 0)
  const finCosts = r('Finance Costs')
  const tax      = r('Income Tax Expense')
  const otherInc = r('Other Income')
  const pat      = (grossProfit + otherInc) - opex - finCosts - tax
  const ppe      = Math.max(r('Property Plant & Equipment') - r('Accumulated Depreciation'), 0)
  const totalAssets = ppe + r('Intangible Assets') + r('Investments') + r('Deferred Tax Asset') +
    r('Inventories') + r('Trade and Other Receivables') +
    r('Tax Receivables') + r('Cash and Cash Equivalents')
  // FIX (29 Jul 2026) — fifth confirmed instance of a recurring bug class
  // (see kailux-fixes-log.md §6.3): every other TB balance in `r()` is
  // legitimately never negative, but Retained Earnings is the one figure
  // that CAN be a genuine accumulated deficit. Wrapping it in abs() (as
  // `r()` does) silently flips a real deficit to a positive number here,
  // inflating Net Equity on this client-facing sign-off portal for exactly
  // the entity whose negative equity most needs to be visible before they
  // sign. Read it raw (signed) instead of through `r()`.
  const retainedEarnings = parseFloat(fig['Retained Earnings'] || 0)
  const equity = r('Stated Capital') + retainedEarnings + pat
  const cash   = r('Cash and Cash Equivalents')
  const currentRatio = (() => {
    const ca = r('Inventories') + r('Trade and Other Receivables') + r('Tax Receivables') + cash
    const cl = r('Trade and Other Payables') + r('Borrowings - Current') + r('Deferred Revenue')
    return cl > 0 ? Math.round((ca / cl) * 10) / 10 : null
  })()
  return { revenue, cogs, grossProfit, pat, totalAssets, equity, cash, currentRatio }
}

// ── PHONE NORMALISER — ensure ZA format ──────────────────────────────────────
// Hoisted out of the SignOff component (29 Jul 2026) — it never referenced
// component state or closures, so it's safe to test in isolation.
export function normalisePhone(raw) {
  const digits = raw.replace(/\D/g, '')
  if (digits.startsWith('0') && digits.length === 10) return `+27${digits.slice(1)}`
  if (digits.startsWith('27') && digits.length === 11) return `+${digits}`
  if (digits.startsWith('+27')) return raw
  return raw
}

// ── GLOBAL STYLES ─────────────────────────────────────────────────────────────
const GLOBAL_STYLE = `
  * { box-sizing: border-box; }
  body { background: ${T.bg}; margin: 0; }
  @keyframes spin  { to { transform: rotate(360deg); } }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes pulse { 0%,100%{ opacity:1; } 50%{ opacity:0.4; } }
  input[type=number]::-webkit-inner-spin-button,
  input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; }
  input[type=number] { -moz-appearance: textfield; }
`

// ── REUSABLE PIECES ───────────────────────────────────────────────────────────
function Logo() {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:'8px', justifyContent:'center', marginBottom:'20px' }}>
      <div style={{ width:'32px', height:'32px', borderRadius:'9px',
        background:T.prg,
        display:'flex', alignItems:'center', justifyContent:'center',
        fontWeight:'900', color:T.onpri, fontSize:'14px', fontFamily:"'Outfit',sans-serif" }}>K</div>
      <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:'17px', fontWeight:'800', color:T.tx }}>
        Kai<span style={{ color:T.or }}>Lux</span>
      </div>
    </div>
  )
}

function Footer({ firmName }) {
  return (
    <div style={{ textAlign:'center', marginTop:'24px', padding:'0 16px 32px',
      fontSize:'12px', color:T.mt, fontFamily:"'Outfit',sans-serif", lineHeight:'1.8' }}>
      {firmName && <div>{firmName}</div>}
      <div>Prepared using KaiLux · IFRS for SMEs · South Africa <Icon name="pin" /></div>
      <div>This portal is encrypted and POPIA compliant.</div>
    </div>
  )
}

function Card({ children, style = {}, animate = false }) {
  return (
    <div style={{ background:T.s2, borderRadius:'16px', border:`1px solid ${T.bd}`,
      boxShadow:T.sh, padding:'28px 24px',
      marginBottom:'14px', animation: animate ? 'fadeUp 0.4s ease' : undefined, ...style }}>
      {children}
    </div>
  )
}

function SectionLabel({ children, color = T.or }) {
  // FIX (July 2026): `children` isn't always a plain string — JSX like
  // `<SectionLabel>{x} literal text</SectionLabel>` passes children as an
  // ARRAY of nodes (`[x, ' literal text']`), not one concatenated string.
  // The old code did `children.toUpperCase()` unconditionally, which threw
  // `TypeError: children.toUpperCase is not a function` for any array-typed
  // children — this crashed the sign-off portal on every load, since
  // exactly one call site here mixes an expression with text. Normalise to
  // a string first so this can't happen regardless of how children is passed.
  const text = Array.isArray(children) ? children.join('') : String(children ?? '')
  return (
    <div style={{ fontSize:'10px', fontWeight:'700', letterSpacing:'1.5px',
      color, marginBottom:'12px', fontFamily:"'Outfit',sans-serif" }}>
      {text.toUpperCase()}
    </div>
  )
}

// ── FINANCIAL FIGURE CARDS ────────────────────────────────────────────────────
function FigureCard({ label, value, prior, colorPositive = T.gn, colorNegative = T.rd, note }) {
  const c = value >= 0 ? colorPositive : colorNegative
  const trend = prior != null ? trendWord(value, prior) : null
  return (
    <div style={{ background:T.bg, borderRadius:'12px', border:`1px solid ${T.bd}`,
      padding:'14px 16px', display:'flex', flexDirection:'column', gap:'4px' }}>
      <div style={{ fontSize:'10px', fontWeight:'700', color:T.mt, letterSpacing:'0.5px',
        fontFamily:"'Outfit',sans-serif" }}>
        {label.toUpperCase()}
      </div>
      <div style={{ fontSize:'22px', fontWeight:'800', color:c,
        fontFamily:"'Outfit',sans-serif", letterSpacing:'-0.5px' }}>
        {fmtRand(value)}
      </div>
      {trend && (
        <div style={{ fontSize:'12px', color: trend.up ? T.gn : T.rd, fontFamily:"'Outfit',sans-serif" }}>
          {trend.up ? '↑' : '↓'} {trend.pct}% vs last year
        </div>
      )}
      {note && <div style={{ fontSize:'12px', color:T.lt, fontFamily:"'Outfit',sans-serif" }}>{note}</div>}
    </div>
  )
}

// ── LOADING SCREEN ────────────────────────────────────────────────────────────
function LoadingScreen() {
  return (
    <div style={{ minHeight:'100vh', background:T.bg, display:'flex',
      alignItems:'center', justifyContent:'center', fontFamily:"'Outfit',sans-serif" }}>
      <style>{GLOBAL_STYLE}</style>
      <div style={{ textAlign:'center' }}>
        <div style={{ width:'40px', height:'40px', margin:'0 auto 16px',
          border:`3px solid ${T.bd}`, borderTopColor:T.or,
          borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/>
        <div style={{ fontSize:'14px', color:T.lt }}>Loading your documents...</div>
      </div>
    </div>
  )
}

// ── ERROR SCREEN ──────────────────────────────────────────────────────────────
function ErrorScreen({ message }) {
  return (
    <div style={{ minHeight:'100vh', background:T.bg, display:'flex', alignItems:'center',
      justifyContent:'center', padding:'24px', fontFamily:"'Outfit',sans-serif" }}>
      <style>{GLOBAL_STYLE}</style>
      <div style={{ maxWidth:'440px', width:'100%', textAlign:'center' }}>
        <Logo />
        <Card>
          <div style={{ marginBottom:'16px' }}><Link2 size={40} strokeWidth={1.5} color={T.lt}/></div>
          <div style={{ fontSize:'18px', fontWeight:'800', color:T.tx, marginBottom:'8px' }}>
            Link not found
          </div>
          <div style={{ fontSize:'14px', color:T.lt, lineHeight:'1.7' }}>{message}</div>
          <div style={{ marginTop:'16px', fontSize:'13px', color:T.mt, lineHeight:'1.6' }}>
            This link may have expired or been used already. Contact your accounting firm if you believe this is an error.
          </div>
        </Card>
      </div>
    </div>
  )
}

// ── ALREADY SIGNED SCREEN ─────────────────────────────────────────────────────
function AlreadySignedScreen({ data }) {
  const firmName = data?.afs_jobs?.clients?.name || ''
  return (
    <div style={{ minHeight:'100vh', background:T.bg, padding:'24px',
      fontFamily:"'Outfit',sans-serif" }}>
      <style>{GLOBAL_STYLE}</style>
      <div style={{ maxWidth:'480px', margin:'0 auto' }}>
        <Logo />
        <Card animate>
          <div style={{ textAlign:'center', padding:'8px 0' }}>
            <div style={{ width:'64px', height:'64px', borderRadius:'50%',
              background:T.gnd, border:`2px solid ${alpha(T.gn, 0.3)}`,
              margin:'0 auto 16px',
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:'28px', color:T.gn }}><Icon name="check" /></div>
            <div style={{ fontSize:'20px', fontWeight:'800', color:T.gn, marginBottom:'8px' }}>
              Already signed
            </div>
            <div style={{ fontSize:'14px', color:T.lt, lineHeight:'1.7', marginBottom:'20px' }}>
              {data?.director_name
                ? `${data.director_name} signed these financial statements.`
                : 'These financial statements have already been signed.'}
              {' '}No further action is needed.
            </div>
            {data?.signed_at && (
              <div style={{ background:T.gnd, border:`1px solid ${alpha(T.gn, 0.3)}`,
                borderRadius:'10px', padding:'12px 16px',
                fontSize:'13px', color:T.gn, lineHeight:'1.6' }}>
                Signed on {new Date(data.signed_at).toLocaleDateString('en-ZA', {
                  weekday:'long', day:'numeric', month:'long', year:'numeric',
                })}
              </div>
            )}
          </div>
        </Card>
        <Footer firmName={firmName} />
      </div>
    </div>
  )
}

// ── REVIEW + SIGN SCREEN — MAIN PORTAL ───────────────────────────────────────
export default function SignOff() {
  const { token } = useParams()
  // FIX (Aug 2026): Africa's Talking isn't connected in production yet —
  // admin-togglable via sms_otp_backup_enabled (System Settings). While
  // off, don't show a phone field that promises an SMS backup the backend
  // can't actually deliver; email remains the only OTP channel.
  const smsBackupEnabled = useFeatureFlag('sms_otp_backup_enabled')

  // ── STATE ─────────────────────────────────────────────────────────────────
  // screens: loading | error | already_signed | portal | otp_entry | signed
  const [screen,       setScreen]       = useState('loading')
  const [afsData,      setAfsData]      = useState(null)
  const [errorMsg,     setErrorMsg]     = useState('')
  const [signedData,   setSignedData]   = useState(null)

  // OTP flow
  const [directorName,  setDirectorName]  = useState('')
  const [directorPhone, setDirectorPhone] = useState('')
  const [nameError,     setNameError]     = useState('')
  const [phoneError,    setPhoneError]    = useState('')
  const [otpDigits,     setOtpDigits]     = useState(['', '', '', '', '', ''])
  const [otpError,      setOtpError]      = useState('')
  const [requestingOTP, setRequestingOTP] = useState(false)
  const [verifying,     setVerifying]     = useState(false)
  const [otpResendCooldown, setOtpResendCooldown] = useState(0)
  const [hasScrolled,   setHasScrolled]   = useState(false)

  const otpRefs = useRef([])
  const cooldownRef = useRef(null)

  // ── LOAD ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!token) { setErrorMsg('No sign-off token found in this link.'); setScreen('error'); return }
    getSignOff(token)
      .then(({ data }) => {
        if (data.status === 'signed' || data.status === 'already_signed') {
          setSignedData(data); setScreen('already_signed')
        } else {
          setAfsData(data); setScreen('portal')
          // Pre-fill director name if available
          const dName = data?.director_name || ''
          if (dName) setDirectorName(dName)
        }
      })
      .catch(err => {
        setErrorMsg(err?.response?.data?.detail || 'This sign-off link is invalid or has expired.')
        setScreen('error')
      })
  }, [token])

  // OTP input focus helpers
  const handleOtpChange = (index, val) => {
    if (!/^\d*$/.test(val)) return
    const newDigits = [...otpDigits]
    newDigits[index] = val.slice(-1)
    setOtpDigits(newDigits)
    setOtpError('')
    if (val && index < 5) otpRefs.current[index + 1]?.focus()
    if (!val && index > 0) otpRefs.current[index - 1]?.focus()
  }

  const handleOtpPaste = (e) => {
    e.preventDefault()
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (paste.length === 6) {
      setOtpDigits(paste.split(''))
      otpRefs.current[5]?.focus()
    }
  }

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus()
    }
  }

  const startCooldown = () => {
    setOtpResendCooldown(60)
    clearInterval(cooldownRef.current)
    cooldownRef.current = setInterval(() => {
      setOtpResendCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownRef.current); return 0 }
        return prev - 1
      })
    }, 1000)
  }

  // ── SEND OTP ──────────────────────────────────────────────────────────────
  const handleRequestOTP = async () => {
    setNameError(''); setPhoneError('')
    if (!directorName.trim()) { setNameError('Please enter your full name as it appears on your ID.'); return }
    // FIX (Jul 2026): phone is optional — email (already on file) is the
    // primary OTP channel. Only validate format if a phone was actually
    // entered; an empty phone is valid and simply skips the SMS backup.
    // FIX (Aug 2026): while sms_otp_backup_enabled is off, the phone field
    // isn't even rendered (see below), so directorPhone is always empty
    // here — skip validation and never send a phone number to the backend.
    let normalised = ''
    if (smsBackupEnabled) {
      const cleaned = directorPhone.replace(/\s/g, '')
      if (cleaned) {
        normalised = normalisePhone(cleaned)
        const digits = normalised.replace(/\D/g, '')
        if (digits.length < 10) { setPhoneError('Please enter a valid South African mobile number, or leave this blank.'); return }
      }
    }
    if (!hasScrolled) setHasScrolled(true)

    setRequestingOTP(true)
    try {
      await requestOTP(token, {
        director_name:  directorName.trim(),
        director_phone: normalised || null,
      })
      setScreen('otp_entry')
      startCooldown()
      setTimeout(() => otpRefs.current[0]?.focus(), 300)
    } catch(err) {
      setPhoneError(err?.response?.data?.detail || 'Could not send your OTP. Please try again.')
    } finally {
      setRequestingOTP(false)
    }
  }

  // ── RESEND OTP ────────────────────────────────────────────────────────────
  const handleResendOTP = async () => {
    if (otpResendCooldown > 0) return
    setOtpDigits(['','','','','','']); setOtpError('')
    setRequestingOTP(true)
    try {
      const cleaned = directorPhone.replace(/\s/g, '')
      await requestOTP(token, {
        director_name:  directorName.trim(),
        director_phone: cleaned ? normalisePhone(cleaned) : null,
      })
      startCooldown()
      otpRefs.current[0]?.focus()
    } catch {
      setOtpError('Could not resend OTP. Please try again.')
    } finally {
      setRequestingOTP(false)
    }
  }

  // ── VERIFY OTP & SIGN ─────────────────────────────────────────────────────
  const handleVerifyAndSign = async () => {
    const otp = otpDigits.join('')
    if (otp.length < 6) { setOtpError('Please enter all 6 digits of your OTP.'); return }
    setVerifying(true); setOtpError('')
    try {
      const cleaned = directorPhone.replace(/\s/g, '')
      const { data } = await verifyAndSign(token, {
        director_name:  directorName.trim(),
        director_phone: cleaned ? normalisePhone(cleaned) : null,
        otp,
      })
      setSignedData(data)
      setScreen('signed')
    } catch(err) {
      const msg = err?.response?.data?.detail || 'Incorrect OTP. Please check and try again.'
      setOtpError(msg)
      // Shake effect — clear digits on wrong OTP
      if (msg.toLowerCase().includes('incorrect') || msg.toLowerCase().includes('invalid') || msg.toLowerCase().includes('expired')) {
        setOtpDigits(['','','','','',''])
        setTimeout(() => otpRefs.current[0]?.focus(), 50)
      }
    } finally {
      setVerifying(false)
    }
  }

  // ── RENDER ────────────────────────────────────────────────────────────────
  if (screen === 'loading')       return <LoadingScreen />
  if (screen === 'error')         return <ErrorScreen message={errorMsg} />
  if (screen === 'already_signed') return <AlreadySignedScreen data={signedData} />

  // ── SIGNED CONFIRMATION ───────────────────────────────────────────────────
  if (screen === 'signed') {
    const firmName = afsData?.afs_jobs?.clients?.name || ''
    return (
      <div style={{ minHeight:'100vh', background:T.bg, padding:'24px',
        fontFamily:"'Outfit',sans-serif" }}>
        <style>{GLOBAL_STYLE}</style>
        <div style={{ maxWidth:'480px', margin:'0 auto' }}>
          <Logo />
          <Card animate style={{ textAlign:'center' }}>
            <div style={{ width:'72px', height:'72px', borderRadius:'50%',
              background:T.gn,
              margin:'0 auto 20px', display:'flex', alignItems:'center',
              justifyContent:'center', fontSize:'32px', color:T.onpri, fontWeight:'800' }}>
              <Icon name="check" />
            </div>
            <div style={{ fontSize:'22px', fontWeight:'800', color:T.gn, marginBottom:'8px' }}>
              Signed successfully
            </div>
            <div style={{ fontSize:'14px', color:T.lt, lineHeight:'1.7', marginBottom:'24px' }}>
              Thank you, <strong style={{ color:T.tx }}>{signedData?.director_name || directorName}</strong>.
              Your sign-off has been recorded and your accounting firm has been notified.
            </div>

            <div style={{ background:T.gnd, border:`1px solid ${alpha(T.gn, 0.3)}`,
              borderRadius:'12px', padding:'18px', textAlign:'left', marginBottom:'20px' }}>
              <SectionLabel color={T.gn}>Audit Trail</SectionLabel>
              {[
                ['Signed by',     signedData?.director_name || directorName],
                ['Verified via',  'OTP sent to mobile number'],
                ['Date & time',   signedData?.signed_at
                  ? new Date(signedData.signed_at).toLocaleString('en-ZA', {
                      weekday:'short', day:'numeric', month:'short',
                      year:'numeric', hour:'2-digit', minute:'2-digit'
                    })
                  : new Date().toLocaleString('en-ZA')],
                ['Document',      `${afsData?.afs_jobs?.clients?.name || ''} Annual Financial Statements`],
              ].map(([k, v], i) => (
                <div key={i} style={{ display:'flex', gap:'10px', padding:'5px 0',
                  borderBottom: i < 3 ? `1px solid ${alpha(T.gn, 0.3)}` : 'none',
                  fontSize:'13px' }}>
                  <span style={{ color:T.lt, minWidth:'100px' }}>{k}</span>
                  <span style={{ fontWeight:'600', color:T.tx }}>{v}</span>
                </div>
              ))}
            </div>

            <div style={{ fontSize:'13px', color:T.mt, lineHeight:'1.6' }}>
              A copy of this sign-off record has been securely stored.
              You may close this window.
            </div>
          </Card>
          <Footer firmName={firmName} />
        </div>
      </div>
    )
  }

  // ── OTP ENTRY SCREEN ──────────────────────────────────────────────────────
  if (screen === 'otp_entry') {
    // FIX (Jul 2026): email is now the primary OTP delivery channel — the
    // director's email is already on file from the original sign-off
    // request (see afsData.director_email), so we mask and show that
    // instead of a phone number, which is now optional and may be blank.
    const directorEmail = afsData?.director_email || ''
    const maskedEmail = (() => {
      const [local, domain] = directorEmail.split('@')
      if (!local || !domain) return 'your email address'
      const maskedLocal = local.length <= 2 ? local[0] + '*' : local[0] + '*'.repeat(local.length - 2) + local.slice(-1)
      return `${maskedLocal}@${domain}`
    })()

    return (
      <div style={{ minHeight:'100vh', background:T.bg, padding:'24px',
        fontFamily:"'Outfit',sans-serif" }}>
        <style>{GLOBAL_STYLE}</style>
        <div style={{ maxWidth:'420px', margin:'0 auto' }}>
          <Logo />
          <Card animate>
            <div style={{ textAlign:'center', marginBottom:'24px' }}>
              <div style={{ marginBottom:'12px' }}><Mail size={34} strokeWidth={1.5} color={T.or}/></div>
              <div style={{ fontSize:'18px', fontWeight:'800', color:T.tx, marginBottom:'8px' }}>
                Enter your OTP
              </div>
              <div style={{ fontSize:'14px', color:T.lt, lineHeight:'1.7' }}>
                We sent a 6-digit code to <strong style={{ color:T.tx }}>{maskedEmail}</strong>.
                It expires in 10 minutes.
              </div>
            </div>

            {/* OTP Input */}
            <div style={{ display:'flex', gap:'8px', justifyContent:'center',
              marginBottom:'20px' }} onPaste={handleOtpPaste}>
              {otpDigits.map((digit, i) => (
                <input
                  key={i}
                  ref={el => otpRefs.current[i] = el}
                  type="number"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleOtpChange(i, e.target.value)}
                  onKeyDown={e => handleOtpKeyDown(i, e)}
                  style={{
                    width:'44px', height:'56px',
                    textAlign:'center', fontSize:'22px', fontWeight:'800',
                    border:`2px solid ${otpError ? T.rd : digit ? T.or : T.bd}`,
                    borderRadius:'10px', outline:'none',
                    background: otpError ? T.rdd : digit ? T.ord : T.s2,
                    color: T.tx, fontFamily:"'Outfit',sans-serif",
                    transition:'border 0.2s, background 0.2s',
                  }}
                />
              ))}
            </div>

            {otpError && (
              <div style={{ background:T.rdd, border:`1px solid ${alpha(T.rd, 0.25)}`,
                borderRadius:'8px', padding:'10px 14px', marginBottom:'16px',
                fontSize:'14px', color:T.rd, textAlign:'center', lineHeight:'1.5' }}>
                {otpError}
              </div>
            )}

            <button
              onClick={handleVerifyAndSign}
              disabled={verifying || otpDigits.join('').length < 6}
              style={{ width:'100%', padding:'14px', borderRadius:'12px', border:'none',
                background: (verifying || otpDigits.join('').length < 6)
                  ? T.mt
                  : T.prg,
                color:T.onpri, fontFamily:"'Outfit',sans-serif",
                fontSize:'15px', fontWeight:'800',
                cursor: verifying || otpDigits.join('').length < 6 ? 'not-allowed' : 'pointer',
                display:'flex', alignItems:'center', justifyContent:'center', gap:'8px',
                transition:'all 0.2s', marginBottom:'16px',
                boxShadow: otpDigits.join('').length === 6 && !verifying
                  ? `0 6px 20px ${alpha(T.pri, 0.35)}` : 'none' }}>
              {verifying ? (
                <>
                  <div style={{ width:'16px', height:'16px',
                    border:`2px solid ${alpha(T.onpri, 0.3)}`,
                    borderTopColor:T.onpri, borderRadius:'50%',
                    animation:'spin 0.8s linear infinite' }}/>
                  Verifying...
                </>
              ) : <><Icon name="sign" /> Confirm Signature</>}
            </button>

            <div style={{ textAlign:'center' }}>
              <button
                onClick={handleResendOTP}
                disabled={otpResendCooldown > 0 || requestingOTP}
                style={{ background:'none', border:'none', fontSize:'14px',
                  color: otpResendCooldown > 0 ? T.mt : T.or,
                  cursor: otpResendCooldown > 0 ? 'default' : 'pointer',
                  fontFamily:"'Outfit',sans-serif", padding:'4px 8px' }}>
                {requestingOTP ? 'Sending...'
                  : otpResendCooldown > 0 ? `Resend code in ${otpResendCooldown}s`
                  : "Didn't receive it? Resend code"}
              </button>
            </div>
            <div style={{ marginTop:'12px', textAlign:'center' }}>
              <button onClick={() => { setScreen('portal'); setOtpDigits(['','','','','','']); setOtpError('') }}
                style={{ background:'none', border:'none', fontSize:'13px', color:T.mt,
                  cursor:'pointer', fontFamily:"'Outfit',sans-serif" }}>
                ← Back to sign-off form
              </button>
            </div>
          </Card>

          <div style={{ textAlign:'center', fontSize:'12px', color:T.mt,
            lineHeight:'1.6', fontFamily:"'Outfit',sans-serif" }}>
            <Icon name="lock" /> Your OTP and signature are end-to-end encrypted.<br/>
            Your email address is stored only to verify this sign-off.
          </div>
        </div>
      </div>
    )
  }

  // ── MAIN PORTAL ───────────────────────────────────────────────────────────
  const clientName    = afsData?.afs_jobs?.clients?.name || 'Company'
  const entityType    = afsData?.afs_jobs?.clients?.entity_type || 'Pty Ltd'
  const yearEnd       = afsData?.year_end || afsData?.afs_jobs?.financial_year || ''
  const engType       = afsData?.afs_jobs?.engagement_type || 'compilation'
  const firmName      = afsData?.firm_name || ''
  const figures       = afsData?.afs_jobs?.figures || {}
  const priorFigures  = afsData?.afs_jobs?.prior_figures || {}
  const natureBiz     = afsData?.nature_of_business || ''
  const hasFigures    = Object.keys(figures).length > 0

  const cur  = hasFigures ? extractFigures(figures)      : null
  const prev = Object.keys(priorFigures).length > 0 ? extractFigures(priorFigures) : null

  const engLabel = ENG_LABELS[engType] || 'Compiled by'

  const whatsappNumber = afsData?.firm_whatsapp || ''
  const whatsappMsg    = encodeURIComponent(
    `Hi, I'm reviewing the financial statements for ${clientName} and have a question.`
  )

  const signatoryTitle = (() => {
    if (entityType === 'CC') return 'Member'
    if (entityType === 'Trust') return 'Trustee'
    if (entityType === 'Sole Proprietor') return 'Owner'
    if (entityType === 'Partnership') return 'Partner'
    return 'Director'
  })()

  return (
    <div style={{ minHeight:'100vh', background:T.bg, fontFamily:"'Outfit',sans-serif" }}>
      <style>{GLOBAL_STYLE}</style>

      {/* ── HEADER BAR ── */}
      <div style={{ background:T.s1, borderBottom:`1px solid ${T.bd}`, padding:'14px 20px',
        display:'flex', alignItems:'center', justifyContent:'space-between',
        position:'sticky', top:0, zIndex:50, boxShadow:T.sh }}>
        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
          <div style={{ width:'26px', height:'26px', borderRadius:'7px',
            background:T.prg,
            display:'flex', alignItems:'center', justifyContent:'center',
            fontWeight:'900', color:T.onpri, fontSize:'13px' }}>K</div>
          <span style={{ fontWeight:'800', color:T.tx, fontSize:'14px' }}>
            Kai<span style={{ color:T.or }}>Lux</span>
          </span>
        </div>
        <div style={{ fontSize:'12px', color:T.mt }}>
          Sign-off Portal
        </div>
      </div>

      <div style={{ maxWidth:'560px', margin:'0 auto', padding:'20px 16px' }}>

        {/* ── INTRO CARD ── */}
        <Card animate>
          <div style={{ marginBottom:'16px' }}>
            <div style={{ fontSize:'12px', fontWeight:'700', color:T.or,
              letterSpacing:'1px', marginBottom:'6px' }}>
              SIGN-OFF REQUEST
            </div>
            <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:'20px',
              fontWeight:'800', color:T.tx, lineHeight:'1.2', marginBottom:'6px' }}>
              {clientName}
            </div>
            <div style={{ fontSize:'14px', color:T.lt, lineHeight:'1.6' }}>
              Annual Financial Statements · {yearEnd}
            </div>
            {firmName && (
              <div style={{ fontSize:'13px', color:T.mt, marginTop:'4px' }}>
                {engLabel} {firmName}
              </div>
            )}
          </div>

          <div style={{ background:T.s3, border:`1px solid ${T.bd}`,
            borderRadius:'10px', padding:'12px 14px',
            fontSize:'13px', color:T.lt, lineHeight:'1.6' }}>
            Your accounting firm has prepared your annual financial statements and needs your
            review and sign-off before they can be issued.
          </div>
        </Card>

        {/* ── FINANCIAL SUMMARY ── */}
        {cur && (
          <Card animate>
            <SectionLabel>Your financial snapshot</SectionLabel>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px',
              marginBottom:'14px' }}>
              <FigureCard
                label="Revenue"
                value={cur.revenue}
                prior={prev?.revenue}
                colorPositive={T.tx}
                note="Total sales for the year"
              />
              <FigureCard
                label={cur.pat >= 0 ? 'Profit' : 'Loss'}
                value={cur.pat}
                prior={prev?.pat}
                note="After all costs and tax"
              />
              <FigureCard
                label="Total Assets"
                value={cur.totalAssets}
                prior={prev?.totalAssets}
                colorPositive={T.tx}
                note="Everything the business owns"
              />
              <FigureCard
                label="Net Equity"
                value={cur.equity}
                prior={prev?.equity}
                note="Owners' stake in the business"
              />
            </div>

            {/* Health indicators */}
            <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
              {cur.cash > 0 && (
                <div style={{ display:'flex', alignItems:'center', gap:'5px',
                  background:T.bg, borderRadius:'20px', padding:'4px 10px',
                  border:`1px solid ${T.bd}`, fontSize:'12px', color:T.lt }}>
                  <span><Icon name="money" /></span>
                  <span>Cash: {fmtRand(cur.cash)}</span>
                </div>
              )}
              {cur.currentRatio && (
                <div style={{ display:'flex', alignItems:'center', gap:'5px',
                  background: cur.currentRatio >= 1.5 ? T.gnd
                    : cur.currentRatio >= 1.0 ? T.amd : T.rdd,
                  borderRadius:'20px', padding:'4px 10px',
                  border:`1px solid ${cur.currentRatio >= 1.5 ? alpha(T.gn, 0.3)
                    : cur.currentRatio >= 1.0 ? alpha(T.am, 0.3) : alpha(T.rd, 0.3)}`,
                  fontSize:'12px',
                  color: cur.currentRatio >= 1.5 ? T.gn
                    : cur.currentRatio >= 1.0 ? T.am : T.rd }}>
                  <span>{cur.currentRatio >= 1.5 ? <Icon name="check" /> : cur.currentRatio >= 1.0 ? <Icon name="alert" /> : <Icon name="alert" />}</span>
                  <span>Liquidity ratio: {cur.currentRatio}x</span>
                </div>
              )}
              {prev && (
                <div style={{ display:'flex', alignItems:'center', gap:'5px',
                  background:T.bld, borderRadius:'20px', padding:'4px 10px',
                  border:`1px solid ${alpha(T.bl, 0.2)}`,
                  fontSize:'12px', color:T.bl }}>
                  <span><Icon name="chart" /></span>
                  <span>Includes prior year comparisons</span>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* ── NATURE OF BUSINESS ── */}
        {natureBiz && (
          <Card>
            <SectionLabel>Business activities</SectionLabel>
            <div style={{ fontSize:'14px', color:T.tx, lineHeight:'1.7' }}>{natureBiz}</div>
          </Card>
        )}

        {/* ── WHATSAPP QUESTIONS ── */}
        {whatsappNumber && (
          <Card>
            <SectionLabel>Questions before signing?</SectionLabel>
            <div style={{ fontSize:'14px', color:T.lt, marginBottom:'12px', lineHeight:'1.6' }}>
              If anything in the financial statements looks unfamiliar or incorrect,
              speak to your accountant before signing.
            </div>
            <a href={`https://wa.me/${whatsappNumber.replace(/\D/g,'')}?text=${whatsappMsg}`}
              target="_blank" rel="noopener noreferrer"
              style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'8px',
                padding:'12px', borderRadius:'10px', border:'none', textDecoration:'none',
                background:'#25D366', color:'white',
                fontFamily:"'Outfit',sans-serif", fontSize:'14px', fontWeight:'700' }}>
              <span><Icon name="chat" /></span> Chat on WhatsApp
            </a>
          </Card>
        )}

        {/* ── SIGN-OFF FORM ── */}
        <Card style={{ border:`1px solid ${alpha(T.or, 0.25)}`, background:T.s2 }}>
          <SectionLabel>{`${signatoryTitle} Sign-off`}</SectionLabel>

          <div style={{ background:T.s3, border:`1px solid ${T.bd}`,
            borderRadius:'10px', padding:'12px 14px', marginBottom:'18px',
            fontSize:'13px', color:T.lt, lineHeight:'1.7' }}>
            By signing, you confirm that you have reviewed these financial statements,
            that they fairly present the financial position of <strong>{clientName}</strong>,
            and that you authorise them for issue.
          </div>

          {/* Name */}
          <div style={{ marginBottom:'14px' }}>
            <label style={{ display:'block', fontSize:'10px', fontWeight:'700',
              color:T.lt, letterSpacing:'1px', marginBottom:'6px' }}>
              YOUR FULL NAME (as on your ID)
            </label>
            <input
              value={directorName}
              onChange={e => { setDirectorName(e.target.value); setNameError('') }}
              placeholder={`e.g. Thavha Michael Madembe`}
              style={{ width:'100%', padding:'13px 14px',
                border:`1.5px solid ${nameError ? T.rd : T.bd}`,
                borderRadius:'10px', fontSize:'15px',
                fontFamily:"'Outfit',sans-serif", color:T.tx, outline:'none',
                background:T.s2, transition:'border 0.2s' }}
              onFocus={e => e.target.style.borderColor = T.or}
              onBlur={e  => e.target.style.borderColor = nameError ? T.rd : T.bd}
            />
            {nameError && (
              <div style={{ fontSize:'13px', color:T.rd, marginTop:'5px' }}>{nameError}</div>
            )}
          </div>

          {/* Phone — only shown once a real SMS provider is connected and
              admin has enabled sms_otp_backup_enabled in System Settings.
              While off, email is the only OTP channel — no field promising
              an SMS backup that can't actually be delivered. */}
          {smsBackupEnabled && (
            <div style={{ marginBottom:'18px' }}>
              <label style={{ display:'block', fontSize:'10px', fontWeight:'700',
                color:T.lt, letterSpacing:'1px', marginBottom:'6px' }}>
                MOBILE NUMBER (OPTIONAL — SMS BACKUP)
              </label>
              <input
                type="tel"
                value={directorPhone}
                onChange={e => { setDirectorPhone(e.target.value); setPhoneError('') }}
                placeholder="e.g. 082 555 1234"
                style={{ width:'100%', padding:'13px 14px',
                  border:`1.5px solid ${phoneError ? T.rd : T.bd}`,
                  borderRadius:'10px', fontSize:'15px',
                  fontFamily:"'Outfit',sans-serif", color:T.tx, outline:'none',
                  background:T.s2, transition:'border 0.2s' }}
                onFocus={e => e.target.style.borderColor = T.or}
                onBlur={e  => e.target.style.borderColor = phoneError ? T.rd : T.bd}
              />
              <div style={{ fontSize:'12px', color:T.mt, marginTop:'5px', lineHeight:'1.5' }}>
                Your 6-digit OTP is sent to your email on file. Add a South African mobile
                number here if you'd also like it sent by SMS as a backup.
              </div>
            </div>
          )}
          {!smsBackupEnabled && (
            <div style={{ fontSize:'12px', color:T.mt, marginBottom:'8px', lineHeight:'1.5' }}>
              Your 6-digit OTP will be sent to your email on file.
            </div>
          )}
          {/* FIX (Aug 2026): phoneError also carries general request-send
              failures (see handleRequestOTP's catch), not just phone-format
              validation — so this must render regardless of whether the
              phone field itself is visible, or a real send failure (e.g.
              "no email on file") would silently show nothing. */}
          {phoneError && (
            <div style={{ fontSize:'13px', color:T.rd, marginBottom:'10px' }}>{phoneError}</div>
          )}

          {/* CTA */}
          <button
            onClick={handleRequestOTP}
            disabled={requestingOTP}
            style={{ width:'100%', padding:'15px', borderRadius:'12px', border:'none',
              background: requestingOTP
                ? T.mt
                : T.prg,
              color:T.onpri, fontFamily:"'Outfit',sans-serif",
              fontSize:'15px', fontWeight:'800', cursor: requestingOTP ? 'not-allowed' : 'pointer',
              boxShadow: requestingOTP ? 'none' : `0 6px 24px ${alpha(T.pri, 0.35)}`,
              display:'flex', alignItems:'center', justifyContent:'center', gap:'8px',
              transition:'all 0.2s' }}>
            {requestingOTP ? (
              <>
                <div style={{ width:'16px', height:'16px',
                  border:`2px solid ${alpha(T.onpri, 0.3)}`,
                  borderTopColor:T.onpri, borderRadius:'50%',
                  animation:'spin 0.8s linear infinite' }}/>
                Sending OTP...
              </>
            ) : (smsBackupEnabled ? '→ Send OTP to My Phone' : '→ Send OTP to My Email')}
          </button>

          <div style={{ marginTop:'12px', fontSize:'12px', color:T.mt,
            textAlign:'center', lineHeight:'1.6' }}>
            <Icon name="lock" /> OTP-verified · Your name, timestamp and IP address form
            the electronic signature audit trail{smsBackupEnabled ? ' (plus your number, if provided)' : ''}.
          </div>
        </Card>

        <Footer firmName={firmName} />
      </div>
    </div>
  )
}
